import { useMemo } from 'react';
import type { Booking } from '@api/types';
import {
  DOW_ES_SHORT,
  ACTIVE_BOOKING_STATUSES,
  getMonthGridStart,
  addDays,
  isoDate,
} from './calendar-shared';

interface CalendarMonthProps {
  date: Date; // any date in the target month
  bookings: readonly Booking[];
  onPickDate: (iso: string) => void;
}

export function CalendarMonth({ date, bookings, onPickDate }: CalendarMonthProps) {
  const targetMonth = date.getMonth();
  const today = isoDate(new Date());

  const cells = useMemo(() => {
    const start = getMonthGridStart(date);
    // 6 rows × 7 cols = 42 days (enough for any month)
    return Array.from({ length: 42 }, (_, i) => {
      const d = addDays(start, i);
      const iso = isoDate(d);
      return {
        iso,
        day: d.getDate(),
        otherMonth: d.getMonth() !== targetMonth,
        today: iso === today,
      };
    });
  }, [date, targetMonth, today]);

  const countByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of bookings) {
      if (!ACTIVE_BOOKING_STATUSES.has(b.status)) continue;
      const day = b.startTime.slice(0, 10);
      m.set(day, (m.get(day) ?? 0) + 1);
    }
    return m;
  }, [bookings]);

  return (
    <div className="cal-month">
      <div className="cal-month-head">
        {DOW_ES_SHORT.map((d) => (
          <div key={d} className="cal-month-dow">{d}</div>
        ))}
      </div>
      <div className="cal-month-grid">
        {cells.map((c) => {
          const active = countByDay.get(c.iso) ?? 0;
          return (
            <button
              key={c.iso}
              type="button"
              className="cal-month-cell"
              data-other-month={c.otherMonth || undefined}
              data-today={c.today || undefined}
              onClick={() => { onPickDate(c.iso); }}
            >
              <div className="cal-month-cell-head">
                <span className="cal-month-day">{c.day}</span>
                {active > 0 && <span className="cal-month-count mono">{active}</span>}
              </div>
              {active > 0 && (
                <div className="cal-month-cell-foot">
                  <span
                    className="cal-month-heatbar"
                    style={{ width: `${String(Math.min(100, active * 12))}%` }}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
